import { useState, useEffect, useRef } from 'react';
import { Routes, BrowserRouter, Route, Outlet, useLocation, Navigate, Link, useNavigate } from "react-router-dom";
import { callApi, ErrorAlert, defaultRootColors } from './crud/utils';
import NoMatch from './NoMatch.js'
import ObjectBrowser from './ObjectBrowser.js';
import SideBarMenu from './SideBarMenu.js';
import { DataComponentWrapper } from './crud/DataComponentWrapper';
import SmallWidthWrapper from "./SmallWidthWrapper";
import useApp, { AppContextProvider } from './AppContext';
import Login from './Login';
import SignUp from './Sign Up/SignUp.js';
import Viewer3D from './crud/Viewer3D.js';
import RfFlowDesigner from './app/RfFlowDesigner.js';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import './App.css';
import ForgotPass from './Forgot Password/ForgotPass.js';
import YardViewInside from './YardView/YardViewInside.js';
import TabletView from './TabletView/TabletView.js';
import ChangePassword from './Change Password/ChangePassword';
import CalendarBooking from './Calendar/CalendarBooking.js';
import HomeYard from './HomeYard';
import EntryWithBooking from './TabletView/Pages/EntryWithBooking.js';
import Exit from './TabletView/Pages/Exit.js';
import { getPathsWithRoles } from './crud/utils';
import Entry from './TabletView/Pages/Entry.js';
import { t } from './i18n'
import { YardViewStatusBar, CalendarStatusBar } from './StatusConfig.js';
import ContrastIcon from '@mui/icons-material/Contrast';

export function ProfileMenu() {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const app = useApp();
    const language = app.sessionToken?.Language;
    let navigate = useNavigate();
    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleThemeChange = (event) => {
        const newTheme = event.target.checked ? 'dark' : 'light';
        app.setTheme(newTheme);
    };
    function logout() {
        app.setUser(null);
        sessionStorage.setItem("appUser", JSON.stringify(null));
        app.setSessionToken(null);
        navigate("/");
    }

    return (
        <div
            className="relative"
            ref={menuRef}
            style={{ display: "flex", justifyContent: "center", alignItems: 'center' }}
        >
            {location.pathname === '/yard-view' && (
                <YardViewStatusBar language={language} />
            )}

            {location.pathname === '/CalendarBooking' && (
                <CalendarStatusBar language={language} />
            )}

            <div
                onClick={toggleMenu}
                className={`header-icon-profile ${isOpen ? 'header-icon-profile-active' : ''}`}
            >
                <i className="bi bi-person"></i>
            </div>

            {isOpen && (
                <div className={`enhanced-profile-dropdown ${app.theme === "dark" ? "DarkTheme DarkThemeFont" : "bg-white"}`}>
                    <div className='profile-dropdown-header'>
                        <div className="profile-header-content">
                            <div className="profile-avatar">
                                {app.user?.name
                                    ?.trim()
                                    .split(/\s+/)
                                    .map(word => word.charAt(0).toUpperCase())
                                    .join('')
                                    || 'U'}
                            </div>
                            <div>
                                <div className="profile-user-name">
                                    {app.user?.name}
                                </div>
                            </div>
                        </div>
                        <div className="profile-login-name">
                            <i className="bi bi-envelope"></i>
                            {app.user?.loginName || "name"}
                        </div>
                        <div className="profile-login-name">
                            {app.sessionToken?.Role === "Admin" ? (
                                <i className="bi bi-shield-lock"></i>) : (<i className="bi bi-person-fill"></i>)}
                            {app.sessionToken?.Role || "Role"}
                        </div>
                    </div>

                    <div className="profile-theme-section">
                        <div className={`profile-menu-divider ${app.theme === "dark" ? "dark" : ""}`}></div>
                        <div className={`profile-theme-toggle ${app.theme === "dark" ? "DarkThemeFont" : ''}`}>
                            <span><ContrastIcon style={{ marginRight: "5px" }} /> {t(app.theme === 'dark' ? 'profile.darkTheme' : 'profile.lightTheme', language)}</span>
                            <label className="switch form-check-label" htmlFor="flexSwitchCheckChecked">
                                <input
                                    type="checkbox"
                                    role="switch"
                                    id="flexSwitchCheckChecked"
                                    checked={app.theme === 'dark'}
                                    onChange={handleThemeChange}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                        <div className={`profile-menu-divider ${app.theme === "dark" ? "dark" : ""}`}></div>
                    </div>

                    <div className="profile-menu-items-container">
                        <Link
                            to="/profile"
                            onClick={() => setIsOpen(false)}
                            style={{ textDecoration: 'none' }}
                        >
                            <div
                                className={`profile-menu-item profile-item ${app.theme === "dark" ? "DarkThemeFont" : ''}`}
                            >
                                <AccountCircleOutlinedIcon sx={{ color: "var(--color-main-background)", fontSize: '22px' }} />
                                <span>{t('profileMenu.profile', language)}</span>
                            </div>
                        </Link>

                        <div className={`profile-menu-divider ${app.theme === "dark" ? "dark" : ""}`}></div>

                        <div
                            onClick={() => {
                                setIsOpen(false);
                                logout();
                            }}
                            className={`profile-menu-item logout-item ${app.theme === "dark" ? "DarkThemeFont" : ''}`}
                        >
                            <ExitToAppOutlinedIcon sx={{ fontSize: '22px' }} />
                            <span>{t('profileMenu.logout', language)}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
function Page() {
    const app = useApp();
    const [error, setError] = useState(null);
    const [isSidebarHovered, setIsSidebarHovered] = useState(false);
    const location = useLocation();
    const [sideMenuOptions, setSideMenuOptions] = useState(app.sideMenuOptions);
    const { fetchAndSetCustomStyle } = useApp();

    const customStyleFetchedRef = useRef(false);

    if (!customStyleFetchedRef.current) {
        fetchAndSetCustomStyle();
        customStyleFetchedRef.current = true;
    }
    const { serverUrl, setSideMenuOptions: updateSideMenu } = app;
    const language = app.sessionToken ? app.sessionToken.Language : "el";

    const updateSideMenuRef = useRef(updateSideMenu);

    useEffect(() => {
        if (serverUrl) {
            async function getLayout() {
                try {
                    const response = await callApi('GET', `${serverUrl}/api/menu?language=${language}`);
                    updateSideMenuRef.current(response);
                    setSideMenuOptions(response);
                } catch (err) {
                    setError(err.message);
                }
            }
            getLayout();
        }
    }, [serverUrl, language]);

    useEffect(() => {
        const savedColors = app.sessionToken?.CustomStyle ? JSON.parse(app.sessionToken.CustomStyle || '{}')?.CustomStyle : defaultRootColors;
        Object.entries(defaultRootColors).forEach(([name, defaultVal]) => {
            const colorVal = savedColors[name] || defaultVal;
            document.documentElement.style.setProperty(name, colorVal);
        });
    }, [app.sessionToken]);

    const isTabletUser = app.user && app.sessionToken.Role === "DoorUser";
    const showSidebarAndLogo = app.user && !isTabletUser;
    const isCalendarPage = location.pathname === "/CalendarBooking";
    const svg = `
  <svg width="1728" height="1117" viewBox="0 0 1728 1117" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_648_487)">
      <rect width="1728" height="1117" fill="var(--bg-primary)"/>
      <path d="M-134 1258C-134 1258 721.917 510.785 1160 555.5C1557.49 596.071 1719.25 155.549 1728.39 65.6775L1726.5 50C1728.66 50.9773 1729.32 56.4985 1728.39 65.6775L1864.5 1196C1280 1091 1975 1578 199.5 1352.5L-134 1258Z"
        fill="${getComputedStyle(document.documentElement).getPropertyValue('--color-main-background').trim()}"/>
    </g>
  </svg>
`;
    const parsedCustomStyle = app.sessionToken?.CustomStyle
        ? JSON.parse(app.sessionToken.CustomStyle)
        : {};

    const logoPath = parsedCustomStyle.Logo
        ? `${app.serverUrl}${parsedCustomStyle.Logo}`
        : 'images/Logo-Yard.png';

    const contentStyle = app.user
        ? isCalendarPage
            ? {
                backgroundColor: app.theme === "dark" ? "rgb(60, 63, 65)" : "#D9D9D9",
                borderRadius: "11px"
            }
            : {
                borderRadius: "11px"
            }
        : { backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}')`, margin: 0, color: "var(--color-main-background)" };
    return (
        <div id="page" className="d-flex flex-column content-background">
            {app.user && (<div id="logo" className="w-100 p-2 text-center">
                <Link to="/" style={{ height: "77px", display: 'flex', alignItems: 'center' }}>
                    <img src={logoPath} alt="Logo-Yard" />
                </Link>
                <ProfileMenu />
            </div>)}
            {!isTabletUser ? (
                <div id="main-content-wrapper" className={`d-flex flex-row flex-grow-1 ${!app.user ? "full-height" : ""}`}>
                    {showSidebarAndLogo && (
                        <nav id="sidebar"
                            onMouseEnter={() => setIsSidebarHovered(true)}
                            onMouseLeave={() => setIsSidebarHovered(false)}
                            className={`${app.theme === "dark" ? "DarkTheme" : "LightTheme"}`}
                        >
                            <div id="sidebar-content">
                                <SideBarMenu options={sideMenuOptions} isSidebarHovered={isSidebarHovered} />
                            </div>
                        </nav>
                    )}

                    <div
                        id="content"
                        style={contentStyle}
                        className={`flex-grow-1 ${!app.user ? "no-border" : ""} ${app.user ? (app.theme === "dark" ? "DarkTheme" : "LightTheme") : ""}`}
                    >
                        <ErrorAlert message={error} setMessage={setError} language={language} />
                        <Outlet />
                    </div>
                </div>
            ) : (
                <>
                    <ErrorAlert message={error} setMessage={setError} language={language} />
                    <Outlet />
                </>
            )}
        </div>
    );
}

function RequireAuth({ children }) {
    const app = useApp();
    let location = useLocation();
    if (!app.user || !sessionStorage.getItem("appUser")) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const allowedPaths = getPathsWithRoles(app?.sideMenuOptions, app.sessionToken?.Role);
    if (!allowedPaths.includes(location.pathname)) {
        if (app.sessionToken?.Role === "DoorUser") {
            return <Navigate to="/tablet-view" replace />;
        }
        else {
            return <Navigate to="/" replace />;
        }
    }
    else if (app.sessionToken?.Role === "DoorUser" && location.pathname === '/') {
        return <Navigate to="/tablet-view" replace />;
    }

    return children;
}

function App() {
    return (
        <AppContextProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Page />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/sign-up" element={<SmallWidthWrapper><SignUp /></SmallWidthWrapper>} />
                        <Route path="/forgot-password" element={<SmallWidthWrapper><ForgotPass userToken="" /></SmallWidthWrapper>} />
                        <Route path="/change-password" element={<ChangePassword />} />
                        {<Route index element={<RequireAuth><HomeYard /></RequireAuth>} />}
                        <Route path="/tablet-view" element={<RequireAuth><TabletView /></RequireAuth>} />
                        <Route path="/tablet-view/entry" element={<RequireAuth><Entry userToken="" /></RequireAuth>} />
                        <Route path="/tablet-view/entry-booking" element={<RequireAuth><EntryWithBooking userToken="" /></RequireAuth>} />
                        <Route path="/tablet-view/exit" element={<RequireAuth><Exit userToken="" /></RequireAuth>} />
                        <Route path="object-browser" element={<RequireAuth><ObjectBrowser /></RequireAuth>} />
                        <Route path="/profile" element={<RequireAuth><Login /></RequireAuth>} />
                        <Route path="yard-view" element={<RequireAuth><YardViewInside userToken="" /></RequireAuth>} />
                        <Route path="list/:layoutId" element={<RequireAuth><DataComponentWrapper componentType='list' /></RequireAuth>} />
                        <Route path="dlist/:layoutId" element={<RequireAuth><DataComponentWrapper componentType='dlist' /></RequireAuth>} />
                        <Route path="form/:layoutId" element={<DataComponentWrapper componentType='form' />} />
                        <Route path="viewer3D" element={<Viewer3D />} />
                        <Route path="composite/:layoutId" element={<RequireAuth><DataComponentWrapper componentType='composite' /></RequireAuth>} />
                        <Route path="RfFlowDesigner" element={<RequireAuth><RfFlowDesigner /></RequireAuth>} />
                        <Route path="CalendarBooking" element={<RequireAuth><CalendarBooking userToken="" /></RequireAuth>} />
                        {/* Using path="*"" means "match anything", so this route
                acts like a catch-all for URLs that we don't have explicit
                routes for. */}
                        <Route path="*" element={<NoMatch />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </AppContextProvider>
    );
}

export default App;
